/**
 * Парсер JSON5 с сохранением комментариев
 */

interface ICommentMap {
  [fieldPath: string]: string;
}

interface IParseResult {
  data: any;
  comments: ICommentMap;
}

/**
 * Парсит JSON5 строку и извлекает комментарии
 */
export const parseJSON5WithComments = (json5String: string): IParseResult => {
  const comments: ICommentMap = {};
  
  // Если строка пуста или null, возвращаем пустой результат
  if (!json5String || typeof json5String !== 'string') {
    return { data: {}, comments: {} };
  }

  const lines = json5String.split('\n');
  let currentComment = '';
  const processedLines: string[] = [];
  const pathStack: string[] = []; // Стек для отслеживания вложенности

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Проверяем, содержит ли строка однострочный комментарий
    if (trimmedLine.startsWith('//')) {
      const commentText = trimmedLine.substring(2).trim();
      if (commentText && commentText !== '*') { // Игнорируем пустые комментарии
        currentComment += commentText + '\n';
      }
      processedLines.push('');
      continue;
    }

    // Проверяем многострочный комментарий
    if (trimmedLine.startsWith('/*')) {
      let commentBlock = line;
      let j = i;
      while (!commentBlock.includes('*/') && j < lines.length - 1) {
        j++;
        commentBlock += '\n' + lines[j];
      }
      // Извлекаем текст комментария
      const match = commentBlock.match(/\/\*([\s\S]*?)\*\//);
      if (match) {
        let commentText = match[1]
          .split('\n')
          .map(l => {
            // Удаляем звездочки и пробелы из начала строк JSDoc комментариев
            return l.trim().replace(/^\*\s*/, '').trim();
          })
          .filter(l => l && l !== '*') // Игнорируем пустые строки и звездочки
          .join('\n');
        
        if (commentText) {
          currentComment += commentText + '\n';
        }
      }
      // Пропускаем строки с многострочным комментарием
      for (let k = i; k <= j; k++) {
        if (k < processedLines.length) {
          processedLines[k] = '';
        } else {
          processedLines.push('');
        }
      }
      i = j;
      continue;
    }

    // Обработка открывающей скобки { для отслеживания вложенности
    if (trimmedLine.includes('{')) {
      const keyMatch = trimmedLine.match(/"([^"]+)"\s*:\s*\{/);
      if (keyMatch) {
        pathStack.push(keyMatch[1]);
      }
    }

    // Обработка закрывающей скобки }
    if (trimmedLine.includes('}')) {
      pathStack.pop();
    }

    // Если строка содержит ключ и у нас есть накопленный комментарий
    if (trimmedLine.includes(':')) {
      // Ищем ключ в кавычках
      const keyMatch = trimmedLine.match(/"([^"]+)"\s*:/);
      if (keyMatch) {
        const key = keyMatch[1];
        const cleanedComment = currentComment.trim();
        if (cleanedComment) {
          // Если мы внутри объекта, используем полный путь
          const fullPath = pathStack.length > 0 ? [...pathStack, key].join('.') : key;
          comments[fullPath] = cleanedComment;
        }
        currentComment = '';
      }
    }

    processedLines.push(line);
  }

  // Преобразуем JSON5 в JSON (удаляем комментарии для парсирования)
  const cleanedJSON5 = processedLines.join('\n');

  let data: any;
  try {
    // eslint-disable-next-line global-require
    const JSON5 = require('json5');
    data = JSON5.parse(cleanedJSON5);
  } catch (error) {
    console.error('Error parsing JSON5:', error);
    console.error('Cleaned JSON5 string:', cleanedJSON5);
    data = {};
  }

  return { data, comments };
};

/**
 * Экспортирует объект обратно в JSON5 с комментариями
 */
export const exportToJSON5WithComments = (data: any, comments: ICommentMap): string => {
  const stringifyWithComments = (
    obj: any,
    indent: number = 0,
    path: string[] = []
  ): string[] => {
    const result: string[] = [];
    const nextIndentStr = '  '.repeat(indent);

    if (obj === null || obj === undefined) {
      return result;
    }

    if (typeof obj !== 'object' || Array.isArray(obj)) {
      return result;
    }

    const keys = Object.keys(obj);

    keys.forEach((key, index) => {
      const fullPath = [...path, key].join('.');
      const value = obj[key];
      const comment = comments[fullPath];

      // Добавляем пустую строку перед комментарием (для читаемости)
      if (index > 0 && comment) {
        result.push('');
      }

      // Добавляем комментарий если есть
      if (comment) {
        const commentLines = comment.split('\n').filter(l => l.trim());
        
        // Проверяем, является ли это многострочным комментарием (содержит @type или другие JSDoc теги)
        const isJSDocComment = commentLines.some(l => l.includes('@'));
        
        if (isJSDocComment || commentLines.length > 1) {
          // Многострочный комментарий в JSDoc формате
          result.push(`${nextIndentStr}/**`);
          commentLines.forEach(commentLine => {
            result.push(`${nextIndentStr} * ${commentLine}`);
          });
          result.push(`${nextIndentStr} */`);
        } else {
          // Однострочный комментарий
          commentLines.forEach(commentLine => {
            result.push(`${nextIndentStr}// ${commentLine}`);
          });
        }
      }

      // Добавляем ключ и значение
      const comma = index < keys.length - 1 ? ',' : '';

      if (value === null) {
        result.push(`${nextIndentStr}"${key}": null${comma}`);
      } else if (typeof value === 'string') {
        result.push(
          `${nextIndentStr}"${key}": "${value.replace(/"/g, '\\"')}"${comma}`
        );
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        result.push(`${nextIndentStr}"${key}": ${value}${comma}`);
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        result.push(`${nextIndentStr}"${key}": {`);
        const nestedLines = stringifyWithComments(value, indent + 1, [...path, key]);
        result.push(...nestedLines);
        result.push(`${nextIndentStr}}${comma}`);
      } else if (Array.isArray(value)) {
        result.push(`${nextIndentStr}"${key}": [${value.map(v => JSON.stringify(v)).join(', ')}]${comma}`);
      }
    });

    return result;
  };

  const lines = stringifyWithComments(data, 1);
  return `{\n${lines.join('\n')}\n}`;
};

/**
 * Получает комментарий для определенного поля
 */
export const getCommentForField = (fieldPath: string, comments: ICommentMap): string => {
  return comments[fieldPath] || '';
};

/**
 * Добавляет или обновляет комментарий для поля
 */
export const setCommentForField = (
  fieldPath: string,
  comment: string,
  comments: ICommentMap
): ICommentMap => {
  return {
    ...comments,
    [fieldPath]: comment
  };
};
